import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useCallback } from "react"
import { supabase } from "./supabase"
import { isDemoSite } from "./isDemoSite"
import { useAuth } from "../components/AuthProvider"

const DEFAULT_LOGO_URL = "/brand/macOS/app-icon-256.png"
const DEFAULT_DASHBOARD_TITLE = "Luster"
export const DEFAULT_DASHBOARD_HERO_LABEL = "Your Luster Portal"
export const DEFAULT_DASHBOARD_HERO_HEADING = "Welcome back"

/** Portal Strip color keys (match dashboard metric colors). */
export const STRIP_COLORS = ["teal", "luster", "red", "violet", "amber"] as const
export type StripColor = (typeof STRIP_COLORS)[number]

/** Portal Strip icon keys (lucide icon names). */
export const STRIP_ICONS = ["sparkles", "star", "zap", "gift", "info"] as const
export type StripIcon = (typeof STRIP_ICONS)[number]

export type PortalStripConfig = {
  text: string
  textMobile: string | null
  buttonText: string
  buttonUrl: string
  buttonColor: StripColor
  buttonHoverColor: StripColor | null
  icon: StripIcon
  iconColor: StripColor
  /** ISO timestamp. When set, the strip is suppressed after this time. Null = permanent. */
  activeUntil: string | null
}

/** Default analog clock seconds hand color (Luster violet). */
export const DEFAULT_ANALOG_CLOCK_SECONDS_COLOR = "#6675c8"

/** Signal palette preset keys and hex for seconds hand dropdown. */
export const ANALOG_CLOCK_SIGNAL_PRESETS = [
  { value: "", label: "Custom", hex: "" },
  { value: "luster", label: "Luster", hex: "#7c3aed" },
  { value: "tasks", label: "Tasks", hex: "#0d9488" },
  { value: "supply", label: "Supply", hex: "#475569" },
  { value: "facility", label: "Facility", hex: "#b91c1c" },
] as const

export type Branding = {
  logoUrl: string
  dashboardTitle: string
  poweredByUrl: string | null
  docsUrl: string | null
  bannerUrl: string | null
  portalStrip: PortalStripConfig | null
  analogClockSecondsColor: string | null
  analogClockOutlineColor: string | null
  analogClockFluidSeconds: boolean
  analogClockShowDigitalTime: boolean
  /** Number of tick marks: 0 = none, 4 = quarters, 8 = eighths, 12 = all. Default 12. */
  analogClockTickMarks: 0 | 4 | 8 | 12
  dashboardHeroLabel: string | null
  dashboardHeroHeading: string | null
  statusPillStatus: "active" | "degraded" | "down" | null
  statusPillLabel: string | null
  statusPillTooltip: string | null
  statusPillLinkUrl: string | null
  glitchEffectsEnabled: boolean
  terminalTraceEnabled: boolean
}

export const STATUS_PILL_STATUS_OPTIONS = ["active", "degraded", "down"] as const
export type StatusPillStatus = (typeof STATUS_PILL_STATUS_OPTIONS)[number]

const STATUS_PILL_LABELS: Record<StatusPillStatus, string> = {
  active: "Active",
  degraded: "Degraded",
  down: "Down",
}
export function getDefaultStatusPillLabel(status: StatusPillStatus): string {
  return STATUS_PILL_LABELS[status]
}

// ---------------------------------------------------------------------------
// Scope types in resolution order (most specific → least specific)
// ---------------------------------------------------------------------------
export type ScopeType = "property" | "client" | "company" | "demo" | "live" | "platform"

// Active scopes: platform (base) → demo (demo site) → company (per-tenant).
// "live" is retired from active use — platform serves as the live base.
// "client" and "property" stay in DB schema for future use but are not resolved here.
const SCOPE_PRIORITY: ScopeType[] = ["property", "client", "company", "demo", "platform"]

// Portal Strip resolves from these scopes (most specific first per SCOPE_PRIORITY).
// Company-scoped strips let tenants message their own users; platform is the creator-level base.
const STRIP_SCOPES: ScopeType[] = ["company", "demo", "platform"]

// ---------------------------------------------------------------------------
// Row shape from the DB
// ---------------------------------------------------------------------------

const SELECT_COLS =
  "id, scope_type, scope_id, logo_url, dashboard_title, powered_by_url, docs_url, banner_url, strip_text, strip_text_mobile, strip_button_text, strip_button_url, strip_button_color, strip_button_hover_color, strip_icon, strip_icon_color, strip_active_until, analog_clock_seconds_color, analog_clock_outline_color, analog_clock_fluid_seconds, analog_clock_show_digital_time, analog_clock_tick_marks, dashboard_hero_label, dashboard_hero_heading, status_pill_status, status_pill_label, status_pill_tooltip, status_pill_link_url, glitch_effects_enabled, terminal_trace_enabled"

interface BrandingRow {
  id: string
  scope_type: string
  scope_id: string | null
  logo_url: string | null
  dashboard_title: string | null
  powered_by_url: string | null
  docs_url: string | null
  banner_url: string | null
  strip_text: string | null
  strip_text_mobile: string | null
  strip_button_text: string | null
  strip_button_url: string | null
  strip_button_color: string | null
  strip_button_hover_color: string | null
  strip_icon: string | null
  strip_icon_color: string | null
  strip_active_until: string | null
  analog_clock_seconds_color: string | null
  analog_clock_outline_color: string | null
  analog_clock_fluid_seconds: boolean | null
  analog_clock_show_digital_time: boolean | null
  analog_clock_tick_marks: number | null
  dashboard_hero_label: string | null
  dashboard_hero_heading: string | null
  status_pill_status: string | null
  status_pill_label: string | null
  status_pill_tooltip: string | null
  status_pill_link_url: string | null
  glitch_effects_enabled: boolean | null
  terminal_trace_enabled: boolean | null
}

// ---------------------------------------------------------------------------
// Attribute-level merge (CSS-cascade style)
// ---------------------------------------------------------------------------

function coalesceStr(...vals: (string | null | undefined)[]): string | null {
  for (const v of vals) {
    const trimmed = v?.trim()
    if (trimmed) return trimmed
  }
  return null
}

function coalesceNum(...vals: (number | null | undefined)[]): number | null {
  for (const v of vals) {
    if (v != null) return v
  }
  return null
}

function coalesceBool(...vals: (boolean | null | undefined)[]): boolean {
  for (const v of vals) {
    if (v != null) return v
  }
  return false
}

function coalesceBoolTrue(...vals: (boolean | null | undefined)[]): boolean {
  for (const v of vals) {
    if (v != null) return v
  }
  return true
}

function parsePortalStrip(rows: BrandingRow[]): PortalStripConfig | null {
  const now = new Date()
  const eligible = rows.filter((r) => STRIP_SCOPES.includes(r.scope_type as ScopeType))
  const sorted = eligible.sort(
    (a, b) => SCOPE_PRIORITY.indexOf(a.scope_type as ScopeType) - SCOPE_PRIORITY.indexOf(b.scope_type as ScopeType),
  )

  for (const row of sorted) {
    const text = row.strip_text?.trim()
    if (!text) continue

    // Skip strips whose override window has passed — fall through to the next scope
    if (row.strip_active_until && new Date(row.strip_active_until) < now) continue

    return {
      text,
      textMobile: row.strip_text_mobile?.trim() || null,
      buttonText: row.strip_button_text?.trim() || text,
      buttonUrl: row.strip_button_url?.trim() || "/",
      buttonColor: (STRIP_COLORS.includes(row.strip_button_color?.trim() as StripColor) ? row.strip_button_color!.trim() : "amber") as StripColor,
      buttonHoverColor: (STRIP_COLORS.includes(row.strip_button_hover_color?.trim() as StripColor) ? row.strip_button_hover_color!.trim() : null) as StripColor | null,
      icon: (STRIP_ICONS.includes(row.strip_icon?.trim() as StripIcon) ? row.strip_icon!.trim() : "sparkles") as StripIcon,
      iconColor: (STRIP_COLORS.includes(row.strip_icon_color?.trim() as StripColor) ? row.strip_icon_color!.trim() : "amber") as StripColor,
      activeUntil: row.strip_active_until ?? null,
    }
  }
  return null
}

function resolveRows(rows: BrandingRow[]): Branding {
  const sorted = [...rows].sort(
    (a, b) => SCOPE_PRIORITY.indexOf(a.scope_type as ScopeType) - SCOPE_PRIORITY.indexOf(b.scope_type as ScopeType),
  )

  const rawStatus = coalesceStr(...sorted.map((r) => r.status_pill_status))?.toLowerCase()
  const statusPillStatus: Branding["statusPillStatus"] =
    rawStatus === "active" || rawStatus === "degraded" || rawStatus === "down" ? rawStatus : null

  return {
    logoUrl: coalesceStr(...sorted.map((r) => r.logo_url)) || DEFAULT_LOGO_URL,
    dashboardTitle: coalesceStr(...sorted.map((r) => r.dashboard_title)) || DEFAULT_DASHBOARD_TITLE,
    poweredByUrl: coalesceStr(...sorted.map((r) => r.powered_by_url)),
    docsUrl: coalesceStr(...sorted.map((r) => r.docs_url)),
    bannerUrl: coalesceStr(...sorted.map((r) => r.banner_url)),
    portalStrip: parsePortalStrip(rows),
    analogClockSecondsColor: coalesceStr(...sorted.map((r) => r.analog_clock_seconds_color)),
    analogClockOutlineColor: coalesceStr(...sorted.map((r) => r.analog_clock_outline_color)),
    analogClockFluidSeconds: coalesceBool(...sorted.map((r) => r.analog_clock_fluid_seconds)),
    analogClockShowDigitalTime: coalesceBool(...sorted.map((r) => r.analog_clock_show_digital_time)),
    analogClockTickMarks: (coalesceNum(...sorted.map((r) => r.analog_clock_tick_marks)) ?? 12) as 0 | 4 | 8 | 12,
    dashboardHeroLabel: coalesceStr(...sorted.map((r) => r.dashboard_hero_label)),
    dashboardHeroHeading: coalesceStr(...sorted.map((r) => r.dashboard_hero_heading)),
    statusPillStatus,
    statusPillLabel: coalesceStr(...sorted.map((r) => r.status_pill_label)),
    statusPillTooltip: coalesceStr(...sorted.map((r) => r.status_pill_tooltip)),
    statusPillLinkUrl: coalesceStr(...sorted.map((r) => r.status_pill_link_url)),
    glitchEffectsEnabled: coalesceBoolTrue(...sorted.map((r) => r.glitch_effects_enabled)),
    terminalTraceEnabled: coalesceBoolTrue(...sorted.map((r) => r.terminal_trace_enabled)),
  }
}

// ---------------------------------------------------------------------------
// Fetch applicable rows for the current context
// ---------------------------------------------------------------------------

interface BrandingContext {
  companyId: string | null
  clientId?: string | null
  propertyId?: string | null
}

async function fetchBrandingRows(ctx: BrandingContext): Promise<BrandingRow[]> {
  const demo = isDemoSite()

  const scopeTypes: ScopeType[] = ["platform"]

  // On demo: only fetch platform + demo rows. Tenant scopes are excluded — demo branding
  // is fully isolated from live tenant config regardless of auth state.
  // On live: platform + company (if user has a companyId). "live" scope retired.
  let query = supabase.from("portal_branding").select(SELECT_COLS)
  if (demo) {
    scopeTypes.push("demo")
    query = query.in("scope_type", scopeTypes)
  } else {
    if (ctx.companyId) scopeTypes.push("company")
    if (ctx.clientId) scopeTypes.push("client")
    if (ctx.propertyId) scopeTypes.push("property")

    const scopeIds: string[] = []
    if (ctx.companyId) scopeIds.push(ctx.companyId)
    if (ctx.clientId) scopeIds.push(ctx.clientId)
    if (ctx.propertyId) scopeIds.push(ctx.propertyId)

    if (scopeIds.length > 0) {
      const orParts = ["scope_id.is.null", ...scopeIds.map((id) => `scope_id.eq.${id}`)]
      query = supabase
        .from("portal_branding")
        .select(SELECT_COLS)
        .or(orParts.join(","))
        .in("scope_type", scopeTypes)
    } else {
      query = query.in("scope_type", scopeTypes)
    }
  }

  const { data, error } = await query
  if (error) {
    console.error("Failed to fetch branding:", error)
    return []
  }

  return (data as unknown as BrandingRow[]) || []
}

// ---------------------------------------------------------------------------
// Default branding (no data)
// ---------------------------------------------------------------------------

const FALLBACK_BRANDING_BASE: Branding = {
  logoUrl: DEFAULT_LOGO_URL,
  dashboardTitle: DEFAULT_DASHBOARD_TITLE,
  poweredByUrl: null,
  docsUrl: null,
  bannerUrl: null,
  portalStrip: null,
  analogClockSecondsColor: null,
  analogClockOutlineColor: null,
  analogClockFluidSeconds: false,
  analogClockShowDigitalTime: true,
  analogClockTickMarks: 12,
  dashboardHeroLabel: null,
  dashboardHeroHeading: null,
  statusPillStatus: null,
  statusPillLabel: null,
  statusPillTooltip: null,
  statusPillLinkUrl: null,
  glitchEffectsEnabled: true,
  terminalTraceEnabled: true,
}

const DEMO_STRIP_PLACEHOLDER: PortalStripConfig = {
  text: "You\u2019re in the demo \u2014 real data, live intelligence. Plans start at $49/mo.",
  textMobile: "Plans from $49/mo",
  buttonText: "Get your portal",
  buttonUrl: "https://demo.luster.company/apply",
  buttonColor: "amber",
  buttonHoverColor: "teal",
  icon: "sparkles",
  iconColor: "amber",
  activeUntil: null,
}

function getFallbackBranding(): Branding {
  if (isDemoSite()) {
    return { ...FALLBACK_BRANDING_BASE, portalStrip: DEMO_STRIP_PLACEHOLDER }
  }
  return FALLBACK_BRANDING_BASE
}

// ---------------------------------------------------------------------------
// localStorage cache — eliminates the placeholder flash on hard refresh
// ---------------------------------------------------------------------------

function getBrandingLsKey(companyId?: string | null): string {
  if (isDemoSite()) return "luster-branding-demo-v2"
  // Include companyId so different tenants (and creator vs admin) don't share
  // the same cache slot and accidentally bleed each other's branding on login.
  return companyId ? `luster-branding-live-v2-${companyId}` : "luster-branding-live-v2"
}

function readBrandingCache(companyId?: string | null): Branding | null {
  try {
    const raw = localStorage.getItem(getBrandingLsKey(companyId))
    if (!raw) return null
    return JSON.parse(raw) as Branding
  } catch {
    return null
  }
}

function writeBrandingCache(branding: Branding, companyId?: string | null): void {
  try {
    localStorage.setItem(getBrandingLsKey(companyId), JSON.stringify(branding))
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

// ---------------------------------------------------------------------------
// React Query key
// ---------------------------------------------------------------------------

const BRANDING_QUERY_KEY = ["portal-branding"] as const

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBranding(opts?: {
  clientId?: string | null
  propertyId?: string | null
}): {
  branding: Branding | null
  loading: boolean
  refetch: () => Promise<void>
} {
  const queryClient = useQueryClient()
  const { companyId: rawCompanyId, isCreator } = useAuth()
  const clientId = opts?.clientId ?? null
  const propertyId = opts?.propertyId ?? null

  // On demo, the query must never depend on auth state — companyId is irrelevant
  // because tenant scopes are excluded from demo branding resolution.
  // Pinning to null prevents auth resolution from changing the query key and
  // triggering a second fetch (and a second flash).
  //
  // For the creator: also pin companyId to null so the creator never resolves
  // company-scoped branding rows. The creator shares companyId with the bootstrap
  // tenant (Luster), so including company scope would cause branding
  // saved by the creator to bleed into the tenant's resolved branding and vice versa.
  // Creator branding resolves only from platform scope (live is retired).
  const companyId = (isDemoSite() || isCreator) ? null : rawCompanyId

  // Use the last-known-good branding from localStorage as initialData so the
  // first render is instant with no flash. Falls back to FALLBACK_BRANDING_BASE
  // on first-ever visit when the cache is empty.
  const initialData = readBrandingCache(companyId) ?? getFallbackBranding()

  const { data: branding, isLoading } = useQuery({
    queryKey: [...BRANDING_QUERY_KEY, companyId, clientId, propertyId],
    queryFn: async () => {
      const rows = await fetchBrandingRows({ companyId, clientId, propertyId })
      const resolved = rows.length === 0 ? getFallbackBranding() : resolveRows(rows)
      writeBrandingCache(resolved, companyId)
      return resolved
    },
    staleTime: Infinity,
    initialData,
    // initialData is always treated as fresh by TanStack Query (doesn't trigger a fetch).
    // Set initialDataUpdatedAt to 0 so TanStack considers it stale and fetches once in
    // the background — we get instant render AND up-to-date data.
    initialDataUpdatedAt: 0,
  })

  useEffect(() => {
    const onBrandingUpdated = () => {
      void queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY })
    }
    window.addEventListener("portal-branding-updated", onBrandingUpdated)
    return () => window.removeEventListener("portal-branding-updated", onBrandingUpdated)
  }, [queryClient])

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY })
  }, [queryClient])

  return { branding: branding ?? null, loading: isLoading, refetch }
}

// ---------------------------------------------------------------------------
// Helper: build the deterministic row ID for a scope
// ---------------------------------------------------------------------------

export function buildBrandingRowId(scopeType: ScopeType, scopeId?: string | null): string {
  if (scopeId) return `${scopeType}:${scopeId}`
  return scopeType
}

// ---------------------------------------------------------------------------
// Helper: fetch a single scope's raw row for editing in Settings
// ---------------------------------------------------------------------------

export async function fetchBrandingRowForScope(
  scopeType: ScopeType,
  scopeId?: string | null,
): Promise<BrandingRow | null> {
  let query = supabase.from("portal_branding").select(SELECT_COLS).eq("scope_type", scopeType)

  if (scopeId) {
    query = query.eq("scope_id", scopeId)
  } else {
    query = query.is("scope_id", null)
  }

  const { data } = await query.maybeSingle()
  return (data as unknown as BrandingRow | null) ?? null
}
